import { type LuxAiApi, Errors } from "@lux-ai/api-interface"
import { getContext, setContext } from "svelte"
import type { Models as M } from "@lux-ai/api-interface"
import type { ProgressController } from "./ProgressHandler.svelte"
import type { DeviceDataGetter, CurrentUserDataGetter, SnapshotData } from "../types"

const LUX_AI_CONTROLLER_CONTEXT_KEY = "lux-ai-controller"
export function setLuxAiController(ctrl: LuxAIController) {
	setContext(LUX_AI_CONTROLLER_CONTEXT_KEY, ctrl)
}
export function getLuxAiController() {
	return getContext<LuxAIController>(LUX_AI_CONTROLLER_CONTEXT_KEY)
}

export type QuickActions = {
	viewScene: (sceneId: number) => void
	jumpToRegister: () => void
	jumpToSubscribe: () => void
	calibrateDevice: (deviceId: string) => void
}

export class LuxAIController {
	api: LuxAiApi
	view: "gallery" | "snapshot" | "generation" = $state("gallery")

	availableThemes: Array<M.Theme> = $state([])
	availableCollections: Array<M.Collection> = $state([])

	themedScenes: Array<M.ThemeScene> = $state([])
	activeGenerations: Array<M.ThemeGeneration> = $state([])

	progressController: ProgressController

	selectedTheme: M.Theme = $state()
	selectedSnapshot: SnapshotData = $state()
	selectedGeneration: M.ThemeGeneration = $state()

	loading: boolean = $state(true)
	quickActions: QuickActions

	userDataGetter: CurrentUserDataGetter
	deviceDataGetter: DeviceDataGetter

	constructor(
		api: LuxAiApi,
		progressController: ProgressController,
		quickActions: QuickActions,
		userDataGetters: CurrentUserDataGetter,
		deviceDataGetters: DeviceDataGetter,
	) {
		this.api = api
		this.quickActions = quickActions
		this.progressController = progressController
		this.userDataGetter = userDataGetters
		this.deviceDataGetter = deviceDataGetters

		this.loadInitialData()
	}

	// #region Themes

	loadInitialData = async () => {
		const promises: Array<Promise<void>> = []

		promises.push(this.refreshThemes())
		promises.push(this.refreshThemedScenes())
		promises.push(this.refreshActiveGenerations())

		await Promise.all(promises)

		this.loading = false
	}

	refreshThemes = async () => {
		const themes = await this.api.fetchThemes()
		this.availableThemes = this.sortThemes(themes)

		this.availableCollections = await this.api.fetchCollections()
	}

	sortThemes = (themes: Array<M.Theme>): Array<M.Theme> => {
		return themes.sort((a, b) => {
			const aTime = a.releaseDate ? new Date(a.releaseDate).getTime() : 0
			const bTime = b.releaseDate ? new Date(b.releaseDate).getTime() : 0
			return bTime - aTime
		})
	}

	// sortCollections = (collections: Array<M.Collection>): Array<M.Collection> => {}

	selectTheme = (theme: M.Theme) => {
		this.selectedTheme = theme
		this.view = "snapshot"
	}

	deselectTheme = () => {
		this.selectedTheme = undefined
		this.view = "gallery"
	}

	cancelGeneration = async (generationId: M.ThemeGeneration["id"]) => {
		await this.api.cancelGeneration(generationId)
		await this.refreshActiveGenerations()
	}

	// #endregion

	refreshThemedScenes = async () => {
		this.userDataGetter.refreshCurrentUserData()
		this.themedScenes = await this.api.fetchGeneratedScenes()
	}

	refreshActiveGenerations = async () => {
		this.activeGenerations = await this.api.fetchActiveGenerations()
		for (const gen of this.activeGenerations) {
			const getProgress = async () => {
				const data = await this.api.fetchGenerationProgress(gen.id)
				return data.progress
			}

			this.progressController.createProgressHandler(gen.id, getProgress, async () => {
				const updatedGen = await this.api.fetchGeneration(gen.id)
				this.updateActiveGeneration(updatedGen)
				this.refreshThemedScenes()
			})
		}
	}

	viewGeneration = (generation: M.ThemeGeneration) => {
		this.selectedGeneration = generation
		this.selectedSnapshot = this.deviceDataGetter.getSnapshotById(generation.snapshot_id)
		this.selectedTheme = this.availableThemes.find((theme) => theme.id === generation.theme_id)
		this.view = "generation"
	}

	updateActiveGeneration = async (gen: M.ThemeGeneration) => {
		const existingIndex = this.activeGenerations.findIndex((g) => g.id == gen.id)
		if (existingIndex > -1) this.activeGenerations.splice(existingIndex, 1, gen)
		else this.activeGenerations.push(gen)
	}

	/**
	 * Set the specified snapshot as the "selected" snapshot for this theme generation.
	 * Throws an error if the snapshot passed is not edited.
	 */
	selectSnapshot = async (snapshot: SnapshotData) => {
		this.selectedSnapshot = snapshot
		this.view = "generation"
	}
}
